import type { ReactNode } from 'react';
import { useAuth } from './useAuth';
import type { OscarRole } from '@/types/oscar';

/**
 * RequireRole — defence-in-depth wrapper for role-gated UI.
 *
 * Server-side checks in /api/* are authoritative. This component is purely
 * UX: when the current user is missing the required role, show a fallback
 * (default: a friendly message) instead of the protected children.
 *
 * Accepts a list of roles; the user needs at least one of them.
 *
 * Example:
 *   <RequireRole anyOf={['oscar-admin', 'oscar-helpdesk']}>
 *     <EditDeviceButton />
 *   </RequireRole>
 */
export function RequireRole({
  anyOf,
  children,
  fallback,
}: {
  anyOf: OscarRole[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { roles, loading } = useAuth();
  if (loading) return null;

  const allowed = anyOf.some((r) => roles.includes(r));
  if (allowed) return <>{children}</>;

  return (
    <>
      {fallback ?? (
        <div className="rounded border border-border bg-white p-4 text-sm text-darkGrey">
          You do not have permission to view this content. Required role(s):{' '}
          <code className="font-mono text-xs">{anyOf.join(', ')}</code>.
        </div>
      )}
    </>
  );
}
